/**
 * What the Contest Overview's fixture surfaces need that the API does not send:
 * the design's badge and pill states, and a crest colour per team.
 *
 * Shared by the Overview tab's Fixtures card (CAE-1546) and the Fixtures
 * section the tab strip switches to (CAE-1573) — which is the point of it being
 * one module: a fixture must draw the same crest and wear the same colour on
 * both, and the two screens are a click apart.
 *
 * Presentation, not domain — a fixture's `settled` is a real and separate thing
 * from `finished`, and this only decides that the *badge* draws them the same.
 * Kept out of `contests/lifecycle.ts` for that reason: that module is about
 * where a contest is, and nothing here changes what a fixture is.
 */
import type { FixtureStatus as ApiFixtureStatus } from '@s2w/types';
import type { CrestTeam, FixtureStatus } from '../../../components/ui';
import { adminTokens, type StatusPillState } from '../../../theme';
import { formatDateTime } from '../../../utils/time';

/**
 * Four backend fixture statuses onto the design's three.
 *
 * `settled` joins `finished`: the badge answers "can players still be scored
 * off this?", and both say no. The distinction between a match that has ended
 * and one our settlement job has processed is real, but it belongs to the
 * settlement screens, not to a dashboard card — and the design has no fourth
 * colour for it.
 *
 * Exhaustive with no `default`, so a fifth fixture status is a compile error
 * here rather than an unstyled badge on screen.
 *
 * Typed off `@s2w/types`' `FixtureStatus` rather than `ContestFixture['status']`
 * for that claim to be worth anything: the latter is the same union hand-copied
 * into `api/contests.ts`, so a status added on the backend would widen one and
 * not the other, this function would fall through to `undefined`, and
 * `FixtureRow` would index its badge palette with it and throw.
 */
export const toFixtureRowStatus = (status: ApiFixtureStatus): FixtureStatus => {
  switch (status) {
    case 'scheduled':
      return 'upcoming';
    case 'live':
      return 'live';
    case 'finished':
    case 'settled':
      return 'finished';
  }
};

/**
 * Four backend fixture statuses onto the **status pill's** palette (CAE-1573).
 *
 * `settled` joins `finished` on the slate swatch for the reason
 * `toFixtureRowStatus` gives — the colour answers "can players still be scored
 * off this?", and both say no — but the two keep separate *labels*, because an
 * operator watching settlement needs to see which fixtures our job has actually
 * processed and the word is the only place that fits.
 *
 * Exhaustive with no `default`, so a fifth fixture status is a compile error
 * rather than a pill indexing its palette with `undefined`.
 */
export const toFixturePillState = (status: ApiFixtureStatus): StatusPillState => {
  switch (status) {
    case 'scheduled':
      return 'scheduled';
    case 'live':
      return 'live';
    case 'finished':
    case 'settled':
      return 'finished';
  }
};

/**
 * A stable colour for a team with no crest and no club colour in the feed.
 *
 * Hashed from the name rather than assigned by position, because position is
 * not stable: the same club is the home side in one contest and the away side
 * in the next, and a colour that moved with the slot would read as two
 * different teams. The hash is a plain character sum — this picks one of six
 * swatches, not a cryptographic digest, and a legible, reproducible answer is
 * the whole requirement.
 *
 * Collisions between the two sides of one fixture are possible and harmless:
 * the row titles itself "{Home} vs {Away}" in text, and the discs are
 * `aria-hidden` decoration either way.
 */
export const crestTeam = (name: string): CrestTeam => {
  const palette = adminTokens.componentColor.crestPalette;
  const hash = [...name].reduce((total, character) => total + character.charCodeAt(0), 0);

  return { name, color: palette[hash % palette.length]! };
};

/**
 * A kickoff as the fixture row writes it — `25 May 2026 · 12:00`.
 *
 * The year stays, unlike `formatCompactDateTime`'s `28 May · 13:17`: that shape
 * is for the Schedule card's dense four-column date row, where the contest's
 * own dates sit days apart and the year is obvious. A fixture is a different
 * object on a different feed, and §6 writes this one long.
 */
export const formatKickoff = (startTime: string | Date): string =>
  formatDateTime(startTime).replace(', ', ' · ');
